import { AccountBalanceOutlined, AccountCircleOutlined, ArrowBack, EventAvailableOutlined, HelpOutline, TaskAlt, ThreePOutlined } from '@mui/icons-material';
import { Box, Button, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { ReactNode, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { DisableContext } from '../../../contexts/DisableContextProvider';
import { headerTypographyProps, itemHeaderStyling } from '../../../styles/sxStyling';
import { UserConfigurableModule } from '../../../types/User';
import { Loading } from '../../../uikit';
import StatusPill from '../../../uikit/pills/StatusPill';
import ItemHeaderInfo, { InfoItem } from './ItemHeaderInfo';
import { styles } from './itemHeaderStyles';
import { EventStatus } from '../../../types/Event';

export type ItemHeaderProps = {
  title: string;
  onSave: () => void;
  archived?: boolean;
  showMoreOptions?: ReactNode;
  disableActions?: boolean;
  module?: UserConfigurableModule;
  icon?: ReactNode;
  infoItems?: InfoItem[];
};

/** Default module icons */
export const DEFAULT_ICONS: Partial<Record<UserConfigurableModule, ReactNode>> = {
  [UserConfigurableModule.ACCOUNTS]: <AccountBalanceOutlined fontSize="medium" color="primary" />,
  [UserConfigurableModule.EVENTS]: <EventAvailableOutlined fontSize="medium" color="primary" />,
  [UserConfigurableModule.TASKS]: <TaskAlt fontSize="medium" color="primary" />,
  [UserConfigurableModule.CONTACTS]: <ThreePOutlined fontSize="medium" color="primary" />,
  [UserConfigurableModule.EMPLOYEES]: <AccountCircleOutlined fontSize="medium" color="primary" />,
};

function MainIcon({
  icon,
  module,
}: {
  icon?: ReactNode;
  module?: UserConfigurableModule;
}) {
  if (icon) return <Box sx={styles.imageIconBox}>{icon}</Box>;
  if (!module) return null;

  const resolved = DEFAULT_ICONS[module] ?? <HelpOutline fontSize="medium" color="primary" />;
  return <Box sx={styles.iconBox}>{resolved}</Box>;
}

export default function ItemHeader({
  title,
  archived = false,
  onSave,
  showMoreOptions,
  disableActions = false,
  module,
  icon,
  infoItems,
}: ItemHeaderProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { disabled: disabledCtx } = useContext(DisableContext);

  const actionsDisabled = disabledCtx || disableActions;
  const handleBack = () => navigate(-1);
  const saveLabel = disableActions ? <Loading size={22} /> : 'Save';

  return (
    <Box sx={itemHeaderStyling}>
      {/* Left: icon + title + info row */}
      <Box sx={styles.leftSection}>
        <MainIcon icon={icon} module={module} />

        <Box sx={styles.titleWrapper}>
          <Box sx={styles.titleRow}>
            <Typography {...headerTypographyProps} sx={styles.titleText}>
              {title}
            </Typography>
            {archived && <StatusPill status={EventStatus.PLANNING} text="Archived" />}
          </Box>

          <ItemHeaderInfo infoItems={infoItems} />
        </Box>
      </Box>

      {/* Right: actions */}
      <Box sx={styles.rightSection}>
        {isMobile && (
          <IconButton onClick={handleBack} sx={styles.mobileBackButton}>
            <ArrowBack />
          </IconButton>
        )}

        <Box sx={styles.actions}>
          {showMoreOptions}
          {!isMobile && (
            <Button variant="outlined" onClick={handleBack}>
              Back
            </Button>
          )}
          <Button variant="contained" onClick={onSave} disabled={!!actionsDisabled}>
            {saveLabel}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
